import * as Yup from "yup";
import { VALIDATION_ENUM } from "enums";

export const treasuryBondsSchema = Yup.object().shape({
  quantity: Yup.string()
    .required("To pole jest wymagane")
    .test("is-integer-min-1", "Minimalna wartość to 1, tylko liczby całkowite", (val) => {
      if (!val) return false;
      const num = Number(val);
      return Number.isInteger(num) && num >= 1;
    }),
  currencyUuid: Yup.string().required("Waluta jest wymagana"),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być większa od 0", (val) => {
      if (val === null || val === undefined || val === "") return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  interestRate: Yup.string()
    .required("To pole jest wymagane")
    .test("is-min-0.01", "Minimalna wartość to 0.01", (val) => {
      if (!val) return false;
      return Number(val) >= 0.01;
    })
    .test("is-max-100", "Maksymalna wartość to 100", (val) => {
      if (!val) return true;
      return Number(val) <= 100;
    }),
  interestType: Yup.string().notRequired(),
  additionalInfo: Yup.string().notRequired(),
  contractFile: Yup.mixed().nullable(),
  relianceCanView: Yup.boolean(),
});

export const cryptoSchema = Yup.object().shape({
  name: Yup.string()
    .required("To pole jest wymagane")
    .min(3, VALIDATION_ENUM.MIN3)
    .max(100, VALIDATION_ENUM.MAX_100)
    .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
  currencyUuid: Yup.string().notRequired(),
  primaryValue: Yup.string()
    .notRequired()
    .test("is-positive-if-filled", "Wartość musi być większa od 0", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  additionalInfo: Yup.string().notRequired(),
  relianceCanView: Yup.boolean(),
});

export const currenciesSchema = Yup.object().shape({
  additionalInfo: Yup.string().notRequired(),
  relianceCanView: Yup.boolean(),
});

export const investmentPolicySchema = Yup.object().shape({
  name: Yup.string()
    .required("To pole jest wymagane")
    .min(3, VALIDATION_ENUM.MIN3)
    .max(100, VALIDATION_ENUM.MAX_100)
    .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  contractDate: Yup.string().notRequired(),
  durationDate: Yup.string().notRequired(),
  durationDateValue: Yup.string().when("durationDate", {
    is: "specific_date",
    then: (schema) => schema.required("To pole jest wymagane"),
    otherwise: (schema) => schema.notRequired(),
  }),
  premiumPayment: Yup.string().notRequired(),
  additionalInfo: Yup.string().notRequired(),
  relianceCanView: Yup.boolean(),
  contractFile: Yup.mixed().nullable(),
});

export const loansSchema = Yup.object().shape({
  loanType: Yup.string().required("To pole jest wymagane"),
  entity: Yup.string()
    .required("To pole jest wymagane")
    .min(3, VALIDATION_ENUM.MIN3)
    .max(100, VALIDATION_ENUM.MAX_100)
    .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  interestRate: Yup.string()
    .required("To pole jest wymagane")
    .test("is-non-negative", "Wartość nie może być ujemna", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) >= 0;
    })
    .test("is-max-999.99", "Wartość nie może być większa niż 999.99", (val) => {
      if (!val) return true;
      return Number(val) <= 999.99;
    }),
  interestRateType: Yup.string().notRequired(),
  interestPayment: Yup.string().notRequired(),
  contractDate: Yup.string().notRequired(),
  durationDate: Yup.string().notRequired(),
  durationDateValue: Yup.string().when("durationDate", {
    is: "specific_date",
    then: (schema) => schema.required("To pole jest wymagane"),
    otherwise: (schema) => schema.notRequired(),
  }),
  autoRenewal: Yup.string().notRequired(),
  additionalInfo: Yup.string().notRequired(),
  relianceCanView: Yup.boolean(),
  contractFile: Yup.mixed().nullable(),
});

export const realEstateSchema = Yup.object().shape({
  propertyType: Yup.string().required("To pole jest wymagane"),
  propertyTypeOther: Yup.string().when("propertyType", {
    is: "other",
    then: (schema) =>
      schema
        .required("To pole jest wymagane")
        .min(3, VALIDATION_ENUM.MIN3)
        .max(100, VALIDATION_ENUM.MAX_100)
        .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
    otherwise: (schema) => schema.notRequired(),
  }),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  area: Yup.string()
    .notRequired()
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  city: Yup.string()
    .notRequired()
    .test("city-optional-format", "", function (val) {
      if (!val) return true;
      if (val.length < 3) return this.createError({ message: VALIDATION_ENUM.MIN3 });
      if (val.length > 100) return this.createError({ message: VALIDATION_ENUM.MAX_100 });
      if (/^\s/.test(val) || /\s$/.test(val))
        return this.createError({ message: VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE });
      return true;
    }),
  postalCode: Yup.string()
    .notRequired()
    .test("postal-code-format", "Kod pocztowy musi mieć format xx-xxx", (val) => {
      if (!val) return true;
      return /^\d{2}-\d{3}$/.test(val);
    }),
  address: Yup.string()
    .notRequired()
    .test("address-optional-format", "", function (val) {
      if (!val) return true;
      if (val.length < 3) return this.createError({ message: VALIDATION_ENUM.MIN3 });
      if (val.length > 100) return this.createError({ message: VALIDATION_ENUM.MAX_100 });
      if (/^\s/.test(val) || /\s$/.test(val))
        return this.createError({ message: VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE });
      return true;
    }),
  additionalInfo: Yup.string().notRequired(),
  contractFile: Yup.mixed().nullable(),
  relianceCanView: Yup.boolean(),
});

export const otherAssetSchema = Yup.object().shape({
  name: Yup.string()
    .required("Pole wymagane")
    .min(3, VALIDATION_ENUM.MIN3)
    .max(100, VALIDATION_ENUM.MAX_100)
    .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
  primaryValue: Yup.string()
    .required("Pole wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("Pole wymagane"),
});

export const preciousStonesSchema = Yup.object().shape({
  name: Yup.string()
    .required("To pole jest wymagane")
    .min(3, VALIDATION_ENUM.MIN3)
    .max(100, VALIDATION_ENUM.MAX_100)
    .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  weight: Yup.string()
    .notRequired()
    .test("weight-min-0.1", "Minimalna wartość to 0.1", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) >= 0.1;
    }),
  cut: Yup.string()
    .notRequired()
    .test("optional-string-format", "", function (val) {
      if (!val) return true;
      if (val.length < 1) return this.createError({ message: VALIDATION_ENUM.MIN1 });
      if (val.length > 100) return this.createError({ message: VALIDATION_ENUM.MAX_100 });
      if (/^\s/.test(val) || /\s$/.test(val))
        return this.createError({ message: VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE });
      return true;
    }),
  color: Yup.string()
    .notRequired()
    .test("optional-string-format", "", function (val) {
      if (!val) return true;
      if (val.length < 1) return this.createError({ message: VALIDATION_ENUM.MIN1 });
      if (val.length > 100) return this.createError({ message: VALIDATION_ENUM.MAX_100 });
      if (/^\s/.test(val) || /\s$/.test(val))
        return this.createError({ message: VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE });
      return true;
    }),
  clarity: Yup.string()
    .notRequired()
    .test("optional-string-format", "", function (val) {
      if (!val) return true;
      if (val.length < 1) return this.createError({ message: VALIDATION_ENUM.MIN1 });
      if (val.length > 100) return this.createError({ message: VALIDATION_ENUM.MAX_100 });
      if (/^\s/.test(val) || /\s$/.test(val))
        return this.createError({ message: VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE });
      return true;
    }),
  additionalInfo: Yup.string().notRequired(),
  contractFile: Yup.mixed().nullable(),
  relianceCanView: Yup.boolean(),
});

export const preciousMetalsSchema = Yup.object().shape({
  propertyType: Yup.string().required("To pole jest wymagane"),
  name: Yup.string().when("propertyType", {
    is: "other",
    then: (schema) =>
      schema
        .required("To pole jest wymagane")
        .min(3, VALIDATION_ENUM.MIN3)
        .max(100, VALIDATION_ENUM.MAX_100)
        .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
    otherwise: (schema) => schema.notRequired(),
  }),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  weight: Yup.string()
    .notRequired()
    .test("weight-min-0.1", "Minimalna wartość to 0.1", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) >= 0.1;
    }),
  fineness: Yup.string()
    .notRequired()
    .test("fineness-format", "Nieprawidłowy format próby", (val) => {
      if (!val) return true;
      if (val.endsWith("%")) return /^\d+(\.\d{1})?%$/.test(val);
      return /^\d+$/.test(val);
    }),
  additionalInfo: Yup.string().notRequired(),
  contractFile: Yup.mixed().nullable(),
  relianceCanView: Yup.boolean(),
});

export const artworkSchema = Yup.object().shape({
  artworkType: Yup.string()
    .required("To pole jest wymagane")
    .min(3, VALIDATION_ENUM.MIN3)
    .max(100, VALIDATION_ENUM.MAX_100)
    .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  name: Yup.string()
    .notRequired()
    .test("optional-string-format", "", function (val) {
      if (!val) return true;
      if (val.length < 3) return this.createError({ message: VALIDATION_ENUM.MIN3 });
      if (val.length > 100) return this.createError({ message: VALIDATION_ENUM.MAX_100 });
      if (/^\s/.test(val) || /\s$/.test(val))
        return this.createError({ message: VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE });
      return true;
    }),
  unknownAuthor: Yup.boolean(),
  author: Yup.string().when("unknownAuthor", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .required("To pole jest wymagane")
        .min(3, VALIDATION_ENUM.MIN3)
        .max(100, VALIDATION_ENUM.MAX_100)
        .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
  }),
  eraAge: Yup.string()
    .notRequired()
    .test("optional-string-format", "", function (val) {
      if (!val) return true;
      if (val.length < 3) return this.createError({ message: VALIDATION_ENUM.MIN3 });
      if (val.length > 100) return this.createError({ message: VALIDATION_ENUM.MAX_100 });
      if (/^\s/.test(val) || /\s$/.test(val))
        return this.createError({ message: VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE });
      return true;
    }),
  additionalInfo: Yup.string().notRequired(),
  contractFile: Yup.mixed().nullable(),
  relianceCanView: Yup.boolean(),
});
